'use client'

import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useLocale } from '@/components/LocaleContext'

/** 360° view: drag horizontally to cycle through image sequence. Uses existing gallery order.
 * Component is used in admin preview and in the frontend.
 */
export function Product360View({
  imageUrls,
  imageAlt,
}: {
  imageUrls: string[]
  imageAlt: string
}) {
  const { locale } = useLocale()
  const tByLocale: Record<
    string,
    {
      dragToRotate: string
      viewImageAriaLabel: (index: number, total: number) => string
    }
  > = {
    en: {
      dragToRotate: 'Drag to rotate',
      viewImageAriaLabel: (index, total) => `360 view, image ${index} of ${total}`,
    },
    fr: {
      dragToRotate: 'Faites glisser pour tourner',
      viewImageAriaLabel: (index, total) => `Vue 360, image ${index} sur ${total}`,
    },
    ar: {
      dragToRotate: 'اسحب للتدوير',
      viewImageAriaLabel: (index, total) => `عرض 360، الصورة ${index} من ${total}`,
    },
  }
  const t = tByLocale[locale] ?? tByLocale.en
  const [frameIndex, setFrameIndex] = useState(0)
  const [isDragging, setIsDragging] = useState(false)
  const startX = useRef(0)
  const startIndex = useRef(0)
  const containerRef = useRef<HTMLDivElement>(null)
  const count = imageUrls.length

  // Preload images for smooth switching
  useEffect(() => {
    imageUrls.forEach((src) => {
      const img = new window.Image()
      img.src = src
    })
  }, [imageUrls])

  const goTo = useCallback(
    (index: number) => {
      const next = ((index % count) + count) % count
      setFrameIndex(next)
    },
    [count],
  )

  const handlePointerDown = useCallback(
    (e: React.MouseEvent | React.TouchEvent) => {
      const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX
      startX.current = clientX
      startIndex.current = frameIndex
      setIsDragging(true)
    },
    [frameIndex],
  )

  const handlePointerMove = useCallback(
    (e: MouseEvent | TouchEvent) => {
      if (!isDragging || count <= 1) return
      if ('touches' in e) e.preventDefault()
      const clientX = 'touches' in e ? e.touches[0].clientX : e.clientX
      const delta = clientX - startX.current
      // ~50px drag = one frame
      const steps = Math.floor(delta / 50)
      goTo(startIndex.current + steps)
    },
    [isDragging, count, goTo],
  )

  const handlePointerUp = useCallback(() => {
    setIsDragging(false)
  }, [])

  useEffect(() => {
    if (!isDragging) return
    window.addEventListener('mousemove', handlePointerMove)
    window.addEventListener('mouseup', handlePointerUp)
    window.addEventListener('touchmove', handlePointerMove, { passive: false })
    window.addEventListener('touchend', handlePointerUp)
    return () => {
      window.removeEventListener('mousemove', handlePointerMove)
      window.removeEventListener('mouseup', handlePointerUp)
      window.removeEventListener('touchmove', handlePointerMove)
      window.removeEventListener('touchend', handlePointerUp)
    }
  }, [isDragging, handlePointerMove, handlePointerUp])

  if (count === 0) return null
  const currentUrl = imageUrls[frameIndex] ?? imageUrls[0]

  return (
    <div className="flex flex-col gap-2">
      <p className="font-poppins text-xs text-neutral-500">
        {t.dragToRotate} · {frameIndex + 1} / {count}
      </p>
      <div
        ref={containerRef}
        className="relative flex-1 min-h-0 aspect-square max-w-lg overflow-hidden rounded-md bg-neutral-100 select-none touch-none cursor-grab active:cursor-grabbing"
        onMouseDown={handlePointerDown}
        onTouchStart={handlePointerDown}
        style={{ touchAction: 'none' }}
        role="img"
        aria-label={t.viewImageAriaLabel(frameIndex + 1, count)}
      >
        {/* eslint-disable-next-line @next/next/no-img-element */}
        <img
          src={currentUrl}
          alt={imageAlt}
          className="absolute inset-0 w-full h-full object-contain object-center pointer-events-none"
          draggable={false}
        />
      </div>
    </div>
  )
}
